import type { XlsxEditCellSnapshot, XlsxEditSnapshot } from './editSnapshot';
import type { XlsxCellValue, XlsxSheet, XlsxWorkbookModel } from './types';

function toWorkbookHyperlink(
  hyperlink: NonNullable<XlsxEditCellSnapshot['hyperlink']>,
): NonNullable<XlsxCellValue['hyperlink']> {
  const out: NonNullable<XlsxCellValue['hyperlink']> = { target: hyperlink.target };
  if (typeof hyperlink.tooltip === 'string' && hyperlink.tooltip) {
    out.tooltip = hyperlink.tooltip;
  }
  return out;
}

function toWorkbookNote(
  note: NonNullable<XlsxEditCellSnapshot['note']>,
): NonNullable<XlsxCellValue['note']> {
  const out: NonNullable<XlsxCellValue['note']> = { text: note.text };
  if (typeof note.author === 'string' && note.author) out.author = note.author;
  return out;
}

/**
 * Merge a renderer edit snapshot for the active sheet into a host-neutral
 * workbook model. Metadata omitted by the renderer is preserved from `prev`;
 * metadata explicitly emitted by the renderer replaces the previous value.
 */
export function mergeXlsxEditSnapshot(
  snapshot: XlsxEditSnapshot,
  prev?: XlsxWorkbookModel | null,
): XlsxWorkbookModel {
  const snapSheet = snapshot.sheet;
  const prevSheet = prev?.sheets.find((sheet) => sheet.name === snapSheet.name);

  const cells: Record<string, XlsxCellValue> = {};
  for (const [a1, cell] of Object.entries(snapSheet.cells)) {
    if (!cell) continue;
    const nextCell: XlsxCellValue = { value: cell.value ?? null };
    if (cell.formula) {
      nextCell.formula = cell.formula.startsWith('=') ? cell.formula : `=${cell.formula}`;
    }
    if (cell.style) nextCell.style = cell.style;

    const prevCell = prevSheet?.cells[a1];
    if (cell.hyperlink !== undefined) {
      if (cell.hyperlink) nextCell.hyperlink = toWorkbookHyperlink(cell.hyperlink);
    } else if (prevCell?.hyperlink) {
      nextCell.hyperlink = prevCell.hyperlink;
    }
    if (cell.note !== undefined) {
      if (cell.note) nextCell.note = toWorkbookNote(cell.note);
    } else if (prevCell?.note) {
      nextCell.note = prevCell.note;
    }
    cells[a1] = nextCell;
  }

  const mergedSheet: XlsxSheet = {
    name: snapSheet.name,
    rowCount: snapSheet.rowCount,
    colCount: snapSheet.colCount,
    cells,
    merges: snapSheet.merges ?? [],
  };

  if (snapSheet.columnWidths !== undefined) {
    mergedSheet.columnWidths = snapSheet.columnWidths;
  } else if (prevSheet?.columnWidths) {
    mergedSheet.columnWidths = prevSheet.columnWidths;
  }
  if (snapSheet.rowHeights !== undefined) {
    mergedSheet.rowHeights = snapSheet.rowHeights;
  } else if (prevSheet?.rowHeights) {
    mergedSheet.rowHeights = prevSheet.rowHeights;
  }
  if (snapSheet.hiddenRows !== undefined) {
    mergedSheet.hiddenRows = snapSheet.hiddenRows;
  } else if (prevSheet?.hiddenRows) {
    mergedSheet.hiddenRows = prevSheet.hiddenRows;
  }
  if (snapSheet.hiddenColumns !== undefined) {
    mergedSheet.hiddenColumns = snapSheet.hiddenColumns;
  } else if (prevSheet?.hiddenColumns) {
    mergedSheet.hiddenColumns = prevSheet.hiddenColumns;
  }
  if (snapSheet.rowOutlineLevels !== undefined) {
    mergedSheet.rowOutlineLevels = snapSheet.rowOutlineLevels;
  } else if (prevSheet?.rowOutlineLevels) {
    mergedSheet.rowOutlineLevels = prevSheet.rowOutlineLevels;
  }

  if (snapSheet.dataValidations !== undefined) {
    mergedSheet.dataValidations = snapSheet.dataValidations.map((validation) => ({
      range: validation.range,
      type: validation.type as NonNullable<XlsxSheet['dataValidations']>[number]['type'],
      allowedValues: validation.allowedValues,
      formula1: validation.formula1 ?? null,
      formula2: validation.formula2 ?? null,
      operator: validation.operator ?? null,
      allowBlank: validation.allowBlank,
      prompt: validation.prompt ?? null,
      promptTitle: validation.promptTitle ?? null,
      error: validation.error ?? null,
      errorTitle: validation.errorTitle ?? null,
    }));
  } else if (prevSheet?.dataValidations) {
    mergedSheet.dataValidations = prevSheet.dataValidations;
  }

  if (snapSheet.autoFilter !== undefined) {
    if (snapSheet.autoFilter) {
      const autoFilter: NonNullable<XlsxSheet['autoFilter']> = {
        ref: snapSheet.autoFilter.ref,
      };
      if (snapSheet.autoFilter.columns?.length) {
        autoFilter.columns = snapSheet.autoFilter.columns;
      }
      mergedSheet.autoFilter = autoFilter;
    } else {
      mergedSheet.autoFilter = null;
    }
  } else if (prevSheet?.autoFilter) {
    mergedSheet.autoFilter = prevSheet.autoFilter;
  }

  if (snapSheet.conditionalFormats !== undefined) {
    mergedSheet.conditionalFormats = snapSheet.conditionalFormats;
  } else if (prevSheet?.conditionalFormats) {
    mergedSheet.conditionalFormats = prevSheet.conditionalFormats;
  }

  if (snapSheet.freeze !== undefined) {
    mergedSheet.freeze =
      snapSheet.freeze && (snapSheet.freeze.rows > 0 || snapSheet.freeze.cols > 0)
        ? { rows: snapSheet.freeze.rows, cols: snapSheet.freeze.cols }
        : null;
  } else if (prevSheet?.freeze) {
    mergedSheet.freeze = prevSheet.freeze;
  }

  let sheets: XlsxSheet[];
  if (prev?.sheets.length) {
    const index = prev.sheets.findIndex((sheet) => sheet.name === snapSheet.name);
    if (index >= 0) {
      sheets = prev.sheets.map((sheet, current) =>
        current === index ? mergedSheet : sheet,
      );
    } else {
      sheets = [...prev.sheets];
      const at = Math.min(Math.max(snapshot.activeSheetIndex, 0), sheets.length);
      sheets.splice(at, 0, mergedSheet);
    }
  } else {
    sheets = [mergedSheet];
  }

  return { sheets, activeSheetIndex: snapshot.activeSheetIndex };
}
